import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import locationService from '../api/locationService';
import apiService from '../api/apiService';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePermissions } from '../hooks/usePermissions';
import { SensitiveContentWrapper, ReadOnlyIndicator } from '../components/ui/PermissionWrapper';

const LocationManager = () => {
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const permissions = usePermissions();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [bulkImportData, setBulkImportData] = useState('');
  const [error, setError] = useState(null);
  const [geocodingAddress, setGeocodingAddress] = useState('');
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [googleMapsEnabled, setGoogleMapsEnabled] = useState(false);
  const [customFields, setCustomFields] = useState([]);

  // Check permissions
  const canManageLocations = permissions.canManage('locations');
  const canViewLocations = permissions.canView('locations');
  const hasReadOnlyAccess = canViewLocations && !canManageLocations;

  // Fetch project settings to get Google Maps API key
  const { data: projectSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['projectSettings', projectId],
    queryFn: () => apiService.getProjectSettings(projectId),
    onSuccess: (data) => {
      console.log('LocationManager received project settings:', data);
      
      // Check if externalServices exists and has googleMaps
      if (data && data.externalServices && data.externalServices.googleMaps) {
        const googleMapsSettings = data.externalServices.googleMaps;
        console.log('Google Maps settings found:', googleMapsSettings);
        
        // DEBUG - Log the exact type and value of the enabled flag
        console.log('Google Maps enabled type:', typeof googleMapsSettings.enabled);
        console.log('Google Maps enabled value:', googleMapsSettings.enabled);
        
        // Set enabled status - forcing boolean conversion and defaulting to false if undefined
        const enabled = googleMapsSettings.enabled === true;
        console.log('Setting Google Maps enabled to:', enabled);
        
        // Force state update with setTimeout to ensure it's applied
        setTimeout(() => {
          setGoogleMapsEnabled(enabled);
        }, 0);
        
        // Set API key if present
        if (googleMapsSettings.apiKey && typeof googleMapsSettings.apiKey === 'string') {
          console.log('Google Maps API key found, setting state');
          setTimeout(() => {
            setGoogleMapsApiKey(googleMapsSettings.apiKey);
          }, 0);
        } else {
          console.log('No Google Maps API key found in settings');
          setGoogleMapsApiKey('');
        }
      } else {
        console.log('No Google Maps settings found, setting defaults');
        setGoogleMapsEnabled(false);
        setGoogleMapsApiKey('');
      }
    },
    onError: (error) => {
      console.error('Error loading project settings:', error);
      setError('Failed to load Google Maps settings');
    }
  });

  // Fetch locations
  const { data: locations = [], isLoading, isError, error: fetchError } = useQuery({
    queryKey: ['locations', projectId],
    queryFn: () => locationService.getLocations(projectId)
  });

  // Save Google Maps API key
  const { mutate: saveGoogleMapsSettings, isLoading: isSavingSettings } = useMutation({
    mutationFn: (data) => {
      console.log('Saving Google Maps settings to server:', data);
      
      // Create a clean object with the settings
      const updatedSettings = {
        externalServices: {
          googleMaps: {
            enabled: Boolean(data.enabled), // Ensure it's a boolean
            apiKey: data.apiKey || ''       // Use empty string if not provided
          }
        }
      };
      
      console.log('Formatted settings to save:', updatedSettings);
      return apiService.updateProjectSettings(projectId, updatedSettings);
    },
    onSuccess: (response) => {
      console.log('Successfully saved Google Maps settings. Response:', response);
      
      // Update the local state to match what was saved
      if (response && response.externalServices && response.externalServices.googleMaps) {
        const savedSettings = response.externalServices.googleMaps;
        setGoogleMapsEnabled(savedSettings.enabled === true);
        if (savedSettings.apiKey) {
          setGoogleMapsApiKey(savedSettings.apiKey);
        }
      }
      
      // Refetch the settings to ensure the UI is in sync with the server
      queryClient.invalidateQueries(['projectSettings', projectId]);
      
      toast.success('Google Maps settings saved successfully!', {
        position: 'top-right',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    },
    onError: (error) => {
      console.error('Failed to save Google Maps settings. Error:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      toast.error(`Failed to save Google Maps settings: ${error.message}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      
      setError(`Failed to save Google Maps settings: ${error.message}`);
    }
  });

  // Create a new location
  const { mutate: createLocation, isLoading: isCreating } = useMutation({
    mutationFn: (data) => locationService.createLocation(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['locations', projectId]);
      setIsFormOpen(false);
      setSelectedLocation(null);
      setError(null);
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to create location');
    }
  });

  // Update a location
  const { mutate: updateLocation, isLoading: isUpdating } = useMutation({
    mutationFn: ({ locationId, data }) => locationService.updateLocation(projectId, locationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['locations', projectId]);
      setIsFormOpen(false);
      setSelectedLocation(null);
      setError(null);
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to update location');
    }
  });

  // Delete a location
  const { mutate: deleteLocation } = useMutation({
    mutationFn: (locationId) => locationService.deleteLocation(projectId, locationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['locations', projectId]);
    }
  });

  // Bulk import locations
  const { mutate: bulkImport, isLoading: isImporting } = useMutation({
    mutationFn: (locations) => locationService.bulkImportLocations(projectId, locations),
    onSuccess: () => {
      queryClient.invalidateQueries(['locations', projectId]);
      setIsImportModalOpen(false);
      setBulkImportData('');
      setError(null);
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Failed to import locations');
    }
  });

  // Geocode address
  const { mutate: geocodeAddress, isLoading: isGeocoding } = useMutation({
    mutationFn: (address) => locationService.geocodeAddress(address, googleMapsApiKey),
    onSuccess: (geocodedData) => {
      try {
        // Format coordinates to ensure they're proper number strings with limited decimal places
        const latitude = parseFloat(geocodedData.coordinates.latitude).toFixed(7);
        const longitude = parseFloat(geocodedData.coordinates.longitude).toFixed(7);
        
        console.log('Geocoded coordinates:', { latitude, longitude });
        
        // Update the form with geocoded data
        const addressInput = document.getElementById('address');
        const latInput = document.getElementById('latitude');
        const lngInput = document.getElementById('longitude');
        
        if (addressInput) addressInput.value = geocodedData.formatted_address;
        
        // Set values and trigger change events to ensure validation is updated
        if (latInput) {
          latInput.value = latitude;
          latInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        if (lngInput) {
          lngInput.value = longitude;
          lngInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        
        setGeocodingAddress('');
      } catch (error) {
        console.error('Error setting geocoded values:', error);
        setError(`Error applying geocoded data: ${error.message}`);
      }
    },
    onError: (err) => {
      setError(`Geocoding failed: ${err.message}`);
      setGeocodingAddress('');
    }
  });

  // Function to force sync UI state with server state
  const syncWithServerState = () => {
    if (projectSettings?.externalServices?.googleMaps) {
      const serverSettings = projectSettings.externalServices.googleMaps;
      console.log('Forcing sync with server state:', serverSettings);
      
      // Update enabled state
      setGoogleMapsEnabled(serverSettings.enabled === true);
      
      // Update API key
      if (serverSettings.apiKey && typeof serverSettings.apiKey === 'string') {
        setGoogleMapsApiKey(serverSettings.apiKey);
      }
      
      toast.info('UI state synchronized with server', {
        position: 'top-right',
        autoClose: 2000
      });
    } else {
      toast.error('No server settings available to sync with', {
        position: 'top-right',
        autoClose: 2000
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    const formData = new FormData(e.target);
    
    // Get and validate coordinates
    const latValue = formData.get('latitude');
    const lngValue = formData.get('longitude');
    let latitude, longitude;
    
    try {
      latitude = parseFloat(latValue);
      if (isNaN(latitude) || latitude < -90 || latitude > 90) {
        setError('Latitude must be a valid number between -90 and 90');
        return;
      }
    } catch (err) {
      setError('Invalid latitude value');
      return;
    }
    
    try {
      longitude = parseFloat(lngValue);
      if (isNaN(longitude) || longitude < -180 || longitude > 180) {
        setError('Longitude must be a valid number between -180 and 180');
        return;
      }
    } catch (err) {
      setError('Invalid longitude value');
      return;
    }
    
    const data = {
      name: formData.get('name'),
      type: formData.get('type'),
      address: formData.get('address'),
      city: formData.get('city'),
      province: formData.get('province'),
      postalCode: formData.get('postalCode'),
      country: formData.get('country'),
      coordinates: {
        latitude,
        longitude
      },
      contactInfo: {
        phone: formData.get('phone'),
        email: formData.get('email'),
        website: formData.get('website'),
        googleMapsLink: formData.get('googleMapsLink')
      },
      services: formData.get('services').split(',').map(s => s.trim()).filter(Boolean),
      description: formData.get('description')
    };

    // Process custom fields into metadata
    const metadata = {};
    customFields.forEach(field => {
      if (field.key && field.key.trim()) {
        metadata[field.key.trim()] = field.value;
      }
    });
    
    // Add metadata to data if there are valid custom fields
    if (Object.keys(metadata).length > 0) {
      data.metadata = metadata;
    }

    // Validate required fields
    if (!data.name) {
      setError('Name is required');
      return;
    }
    if (!data.address) {
      setError('Address is required');
      return;
    }
    
    // Validate custom fields
    if (!validateCustomFields()) {
      return;
    }
    
    // Log the data being submitted
    console.log('Submitting location data:', data);

    if (selectedLocation) {
      updateLocation({ locationId: selectedLocation._id, data });
    } else {
      createLocation(data);
    }
  };

  const handleBulkImport = () => {
    try {
      const locations = JSON.parse(bulkImportData);
      if (!Array.isArray(locations)) {
        setError('Data must be a JSON array of locations');
        return;
      }
      bulkImport(locations);
    } catch (err) {
      setError('Invalid JSON format');
    }
  };

  const handleSaveGoogleMapsSettings = () => {
    console.log('handleSaveGoogleMapsSettings called');
    console.log('Current values: enabled=', googleMapsEnabled, 'apiKey=', googleMapsApiKey ? '[PRESENT]' : '[EMPTY]');
    
    saveGoogleMapsSettings({
      enabled: googleMapsEnabled,
      apiKey: googleMapsApiKey
    });
  };

  const handleGeocodeAddress = () => {
    if (!geocodingAddress) {
      setError('Address is required for geocoding');
      return;
    }
    if (!googleMapsApiKey) {
      setError('Google Maps API key is required for geocoding');
      return;
    }
    geocodeAddress(geocodingAddress);
  };

  // Ensure Google Maps settings are applied when projectSettings change
  useEffect(() => {
    if (projectSettings?.externalServices?.googleMaps) {
      const serverSettings = projectSettings.externalServices.googleMaps;
      console.log('useEffect: Applying Google Maps settings from server:', serverSettings);
      
      // Set enabled state
      const enabled = serverSettings.enabled === true;
      setGoogleMapsEnabled(enabled);
      
      // Set API key if present
      if (serverSettings.apiKey && typeof serverSettings.apiKey === 'string') {
        setGoogleMapsApiKey(serverSettings.apiKey);
      }
    }
  }, [projectSettings]);

  // Handle custom fields operations
  const addCustomField = () => {
    setCustomFields([...customFields, { key: '', value: '' }]);
  };

  const removeCustomField = (index) => {
    const newFields = customFields.filter((_, i) => i !== index);
    setCustomFields(newFields);
  };

  const updateCustomField = (index, field, value) => {
    const newFields = [...customFields];
    newFields[index][field] = value;
    setCustomFields(newFields);
  };

  // Validate custom fields for duplicate keys
  const validateCustomFields = () => {
    const keys = customFields.map(field => field.key.trim()).filter(key => key);
    const uniqueKeys = new Set(keys);
    
    if (keys.length !== uniqueKeys.size) {
      setError('Duplicate keys found in custom fields. Each key must be unique.');
      return false;
    }
    
    return true;
  };

  // Common custom field suggestions
  const commonFieldSuggestions = [
    { key: 'locationId', description: 'Unique identifier for the location' },
    { key: 'branchCode', description: 'Branch or office code' },
    { key: 'departmentId', description: 'Department identifier' },
    { key: 'facilityType', description: 'Type of facility or service' },
    { key: 'operatingHours', description: 'Special operating hours info' },
    { key: 'capacity', description: 'Capacity or size information' },
    { key: 'priority', description: 'Priority level (high, medium, low)' }
  ];

  // Reset custom fields when form opens/closes
  useEffect(() => {
    if (isFormOpen) {
      if (selectedLocation?.metadata) {
        // Convert metadata object to array of key-value pairs
        const metadataEntries = Object.entries(selectedLocation.metadata);
        if (metadataEntries.length > 0) {
          setCustomFields(metadataEntries.map(([key, value]) => ({ key, value: String(value) })));
        } else {
          setCustomFields([]);
        }
      } else {
        setCustomFields([]);
      }
    }
  }, [isFormOpen, selectedLocation]);

  return (
    <Layout>
      <div className="dashboard">
        <div className="dashboard-header">
          <h1 className="page-title">Location Manager</h1>
          {canManageLocations && (
            <div className="flex space-x-2">
              <button
                className="btn btn-secondary text-smd"
                onClick={() => setIsImportModalOpen(true)}
              >
                Bulk Import
              </button>
              <button
                className="btn btn-primary text-smd ml-2"
                onClick={() => {
                  setSelectedLocation(null);
                  setIsFormOpen(true);
                }}
              >
                Add New Location
              </button>
            </div>
          )}
        </div>

        {/* Toast notifications */}
        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover draggable />

        {/* Google Maps API Configuration */}
        <SensitiveContentWrapper>
          <div className="card mt-4 p-4">
            <h2 className="text-lg font-medium mb-4">Google Maps API Configuration</h2>
            <p className="text-sm text-gray-600 mb-4">
              Configure Google Maps API to enable precise distance and travel time calculations.
            </p>
            <div className="space-y-4">
              <div className="form-group flex">
                <label className="form-label items-center">
                  <input
                    type="checkbox"
                    checked={googleMapsEnabled}
                    onChange={(e) => {
                      console.log('Setting Google Maps enabled to:', e.target.checked);
                      setGoogleMapsEnabled(e.target.checked);
                    }}
                    className="verticalAlignTextTop scale1-25"
                    disabled={isLoadingSettings || hasReadOnlyAccess}
                  />
                  <span className="ml-2 items-center">
                    {isLoadingSettings ? 'Loading...' : 'Enable Google Maps integration'}
                    {googleMapsEnabled && (
                      <span className="ml-2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        Enabled
                      </span>
                    )}
                  </span>
                </label>
                {projectSettings?.externalServices?.googleMaps && 
                 (googleMapsEnabled !== projectSettings.externalServices.googleMaps.enabled) && (
                  <p className="text-xs text-orange-500 mt-1 ml-6">
                    Warning: Local enabled state ({googleMapsEnabled ? 'enabled' : 'disabled'}) differs 
                    from server ({projectSettings.externalServices.googleMaps.enabled ? 'enabled' : 'disabled'})!
                  </p>
                )}
              </div>
              {googleMapsEnabled && (
                <div className="form-group">
                  <label className="form-label">Google Maps API Key</label>
                  <div className="relative">
                    <input
                      type="password"
                      value={googleMapsApiKey || ''}
                      onChange={(e) => setGoogleMapsApiKey(e.target.value)}
                      className="form-input"
                      placeholder={isLoadingSettings ? "Loading..." : "Enter your Google Maps API key"}
                      disabled={isLoadingSettings || hasReadOnlyAccess}
                    />
                    {googleMapsApiKey && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                        Key Set
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {projectSettings?.externalServices?.googleMaps?.apiKey && 
                     googleMapsApiKey !== projectSettings?.externalServices?.googleMaps?.apiKey ?
                      'Warning: Local value differs from server value!' : ''}
                  </p>
                </div>
              )}
              {!hasReadOnlyAccess && (
                <button
                  className="btn btn-primary text-smd"
                  onClick={handleSaveGoogleMapsSettings}
                  disabled={isSavingSettings}
                >
                  {isSavingSettings ? 'Saving...' : 'Save Google Maps Settings'}
                </button>
              )}
              
              {/* Add sync button */}
              {!hasReadOnlyAccess && (projectSettings?.externalServices?.googleMaps && 
               (projectSettings.externalServices.googleMaps.enabled !== googleMapsEnabled ||
                projectSettings.externalServices.googleMaps.apiKey !== googleMapsApiKey)) && (
                <button
                  className="btn btn-secondary ml-2"
                  onClick={syncWithServerState}
                  title="Reset to server values"
                >
                  Sync with Server
                </button>
              )}
              
              {/* Debug info - only visible in development mode */}
              {/*
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-4 p-2 bg-gray-100 rounded-md text-xs font-mono">
                  <h4 className="font-bold mb-1">Debug Info:</h4>
                  <div><strong>Project ID:</strong> {projectId}</div>
                  <div><strong>Enabled State (UI):</strong> {String(googleMapsEnabled)} ({typeof googleMapsEnabled})</div>
                  <div><strong>API Key Present (UI):</strong> {googleMapsApiKey ? 'Yes' : 'No'}</div>
                  <div><strong>API Key Length:</strong> {googleMapsApiKey ? googleMapsApiKey.length : 0}</div>
                  <div><strong>Loading Settings:</strong> {String(isLoadingSettings)}</div>
                  <div><strong>Saving Settings:</strong> {String(isSavingSettings)}</div>
                  <div><strong>Server Data:</strong></div>
                  <pre>{JSON.stringify(projectSettings?.externalServices?.googleMaps || 'Not loaded', null, 2)}</pre>
                  <div className="mt-2"><strong>Settings Match?</strong></div>
                  <div>Enabled: {
                    projectSettings?.externalServices?.googleMaps?.enabled === googleMapsEnabled 
                    ? '✅ Matching' 
                    : '❌ Mismatch'
                  }</div>
                  <div>API Key: {
                    projectSettings?.externalServices?.googleMaps?.apiKey === googleMapsApiKey 
                    ? '✅ Matching' 
                    : '❌ Mismatch'
                  }</div>
                </div>
              )}*/}
            </div>
          </div>
        </SensitiveContentWrapper>

        {/* Location Form */}
        {isFormOpen && (
          <div className="card mt-4 p-4">
            <h2 className="text-lg font-medium mb-4">
              {selectedLocation ? 'Edit Location' : 'New Location'}
            </h2>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <strong>Error:</strong> {error}
              </div>
            )}

            {!hasReadOnlyAccess && (
              <div className="mb-4">
                <h3 className="text-md font-medium mb-2">Geocode Address</h3>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={geocodingAddress}
                    onChange={(e) => setGeocodingAddress(e.target.value)}
                    className="form-input flex-1"
                    placeholder="Enter address to geocode"
                  />
                  <button
                    type="button"
                    className="btn btn-secondary text-smd ml-2"
                    onClick={handleGeocodeAddress}
                    disabled={isGeocoding || !googleMapsApiKey}
                  >
                    {isGeocoding ? 'Geocoding...' : 'Geocode'}
                  </button>
                </div>
                {!googleMapsApiKey && (
                  <p className="text-xs text-yellow-600 mt-1">
                    Google Maps API key is required for geocoding
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="location-form-group">
                  <label className="form-label">Name*</label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    className="form-input"
                    defaultValue={selectedLocation?.name}
                    required
                    disabled={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Type*</label>
                  <input
                    type="text"
                    name="type"
                    id="type"
                    className="form-input"
                    defaultValue={selectedLocation?.type || 'hospital'}
                    placeholder="e.g., hospital, branch, office"
                    required
                    disabled={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group col-span-1 md:col-span-2">
                  <label className="form-label">Address*</label>
                  <input
                    type="text"
                    name="address"
                    id="address"
                    className="form-input"
                    defaultValue={selectedLocation?.address}
                    required
                    disabled={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">City</label>
                  <input
                    type="text"
                    name="city"
                    id="city"
                    className="form-input"
                    defaultValue={selectedLocation?.city}
                    disabled={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Province</label>
                  <input
                    type="text"
                    name="province"
                    id="province"
                    className="form-input"
                    defaultValue={selectedLocation?.province}
                    disabled={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Postal Code</label>
                  <input
                    type="text"
                    name="postalCode"
                    id="postalCode"
                    className="form-input"
                    defaultValue={selectedLocation?.postalCode}
                    disabled={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    name="country"
                    id="country"
                    className="form-input"
                    defaultValue={selectedLocation?.country || 'Indonesia'}
                    disabled={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Latitude*</label>
                  <input
                    type="number"
                    step="any"
                    name="latitude"
                    id="latitude"
                    className="form-input"
                    defaultValue={selectedLocation?.coordinates?.latitude}
                    required
                    min="-90"
                    max="90"
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Longitude*</label>
                  <input
                    type="number"
                    step="any"
                    name="longitude"
                    id="longitude"
                    className="form-input"
                    defaultValue={selectedLocation?.coordinates?.longitude}
                    required
                    min="-180"
                    max="180"
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Phone</label>
                  <input
                    type="text"
                    name="phone"
                    id="phone"
                    className="form-input"
                    defaultValue={selectedLocation?.contactInfo?.phone}
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="form-input"
                    defaultValue={selectedLocation?.contactInfo?.email}
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group col-span-1 md:col-span-2">
                  <label className="form-label">Website</label>
                  <input
                    type="url"
                    name="website"
                    id="website"
                    className="form-input"
                    defaultValue={selectedLocation?.contactInfo?.website}
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group col-span-1 md:col-span-2">
                  <label className="form-label">Google Maps Link</label>
                  <input
                    type="url"
                    name="googleMapsLink"
                    id="googleMapsLink"
                    className="form-input"
                    defaultValue={selectedLocation?.contactInfo?.googleMapsLink}
                    placeholder="e.g., https://maps.google.com/?q=your-location"
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                </div>

                <div className="location-form-group col-span-1 md:col-span-2">
                  <label className="form-label">Services (comma separated)</label>
                  <input
                    type="text"
                    name="services"
                    id="services"
                    className="form-input"
                    defaultValue={selectedLocation?.services?.join(', ')}
                    placeholder="e.g., emergency, maternity, cardiology"
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  />
                </div>

                <div className="form-group col-span-1 md:col-span-2">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    id="description"
                    className="form-textarea w-full h-100"
                    rows="3"
                    defaultValue={selectedLocation?.description}
                    disabled={hasReadOnlyAccess}
                    readOnly={hasReadOnlyAccess}
                  ></textarea>
                </div>

                {/* Custom Fields Section */}
                {customFields.length > 0 && (
                  <div className="form-group col-span-1 md:col-span-2">
                    <div className="flex justify-between items-center mb-3">
                      <label className="form-label">Custom Fields</label>
                      {!hasReadOnlyAccess && (
                        <button
                          type="button"
                          onClick={addCustomField}
                          className="btn btn-sm btn-secondary text-smd"
                        >
                          + Add Field
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Add custom key-value pairs that will be passed to the chatbot when location information is requested.
                      Example: key="locationId", value="183838"
                    </p>
                    <div className="space-y-2">
                      {customFields.map((field, index) => (
                        <div key={index} className="flex space-x-2">
                          <input
                            type="text"
                            value={field.key}
                            onChange={(e) => updateCustomField(index, 'key', e.target.value)}
                            className="form-input flex-1"
                            placeholder="Key (e.g., locationId, branchCode)"
                            disabled={hasReadOnlyAccess}
                            readOnly={hasReadOnlyAccess}
                          />
                          <input
                            type="text"
                            value={field.value}
                            onChange={(e) => updateCustomField(index, 'value', e.target.value)}
                            className="form-input flex-1"
                            placeholder="Value (e.g., 183838, BR001)"
                            disabled={hasReadOnlyAccess}
                            readOnly={hasReadOnlyAccess}
                          />
                          {!hasReadOnlyAccess && (
                            <button
                              type="button"
                              onClick={() => removeCustomField(index)}
                              className="btn btn-sm btn-danger text-smd"
                              title="Remove this field"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add Custom Field Button - shown when no fields exist */}
                {customFields.length === 0 && (
                  <div className="form-group col-span-1 md:col-span-2">
                    <div className="text-center py-4 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-sm text-gray-500 mb-3">
                        No custom fields added yet. {!hasReadOnlyAccess ? 'Add custom key-value pairs that will be passed to the chatbot.' : ''}
                      </p>
                      {!hasReadOnlyAccess && (
                        <button
                          type="button"
                          onClick={addCustomField}
                          className="btn btn-sm btn-secondary text-smd"
                        >
                          + Add Custom Field
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {hasReadOnlyAccess && (
                <ReadOnlyIndicator />
              )}

              <div className="flex gap-2 mt-4">
                {!hasReadOnlyAccess && (
                  <button
                    type="submit"
                    className="btn btn-primary text-smd"
                    disabled={isCreating || isUpdating}
                  >
                    {isCreating || isUpdating
                      ? 'Saving...'
                      : selectedLocation
                      ? 'Update Location'
                      : 'Create Location'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary text-smd ml-2"
                  onClick={() => {
                    setIsFormOpen(false);
                    setSelectedLocation(null);
                    setError(null);
                  }}
                >
                  {hasReadOnlyAccess ? 'Close' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Bulk Import Modal */}
        {isImportModalOpen && (
          <div className="card mt-4 p-4">
            <h2 className="text-lg font-medium mb-4">Bulk Import Locations</h2>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <strong>Error:</strong> {error}
              </div>
            )}

            <div className="form-group">
              <label className="form-label">JSON Data</label>
              <textarea
                value={bulkImportData}
                onChange={(e) => setBulkImportData(e.target.value)}
                className="form-textarea"
                rows="8"
                placeholder='[
  {
    "name": "Hospital A",
    "type": "hospital",
    "address": "123 Main St",
    "coordinates": { "latitude": -6.123, "longitude": 106.456 },
    "contactInfo": { 
      "phone": "123-456-7890",
      "googleMapsLink": "https://maps.google.com/?q=-6.123,106.456" 
    },
    "metadata": {
      "locationId": "183838",
      "branchCode": "BR001"
    }
  }
]'
              ></textarea>
            </div>

            <div className="flex gap-2 mt-4">
              <button
                className="btn btn-primary"
                onClick={handleBulkImport}
                disabled={isImporting}
              >
                {isImporting ? 'Importing...' : 'Import Locations'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setIsImportModalOpen(false);
                  setBulkImportData('');
                  setError(null);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Locations List */}
        <div className="card mt-4">
          <h2 className="text-lg font-medium mb-4">Locations</h2>

          {isLoading ? (
            <p>Loading locations...</p>
          ) : isError ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p>
                <strong>Error loading locations:</strong> {fetchError?.message}
              </p>
              <button
                className="mt-2 btn btn-sm btn-primary"
                onClick={() => queryClient.invalidateQueries(['locations', projectId])}
              >
                Retry Loading
              </button>
            </div>
          ) : locations.length === 0 ? (
            <p>No locations found. Add one to get started!</p>
          ) : (
            <div className="space-y-4">
              {locations.map((location) => (
                <div key={location._id} className="border rounded p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{location.name}</h3>
                      <p className="text-sm text-gray-600">{location.address}</p>
                      <div className="mt-2">
                        <span className="badge badge-primary">{location.type}</span>
                        {location.city && (
                          <span className="badge badge-secondary ml-2">{location.city}</span>
                        )}
                        {location.services && location.services.length > 0 && (
                          <div className="mt-1">
                            {location.services.map((service, index) => (
                              <span
                                key={index}
                                className="badge badge-outline mr-1 mb-1"
                              >
                                {service}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        <span>
                          Coordinates: {location.coordinates.latitude}, {location.coordinates.longitude}
                        </span>
                      </div>
                      {location.contactInfo?.googleMapsLink && (
                        <div className="text-xs text-blue-500 mt-1">
                          <a href={location.contactInfo.googleMapsLink} target="_blank" rel="noopener noreferrer" className="flex items-center">
                            <span className="underline">Google Maps</span>
                          </a>
                        </div>
                      )}
                      {/* Custom Fields Display */}
                      {location.metadata && Object.keys(location.metadata).length > 0 && (
                        <div className="text-xs text-gray-500 mt-2">
                          <div className="font-medium mb-1">Custom Fields:</div>
                          <div className="space-y-1">
                            {Object.entries(location.metadata).map(([key, value]) => (
                              <div key={key} className="flex">
                                <span className="font-medium mr-1">{key}:</span>
                                <span className="text-gray-600">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="space-x-2">
                      <button
                        className="btn btn-sm btn-secondary text-smd"
                        onClick={() => {
                          setSelectedLocation(location);
                          setIsFormOpen(true);
                        }}
                      >
                        {canManageLocations ? 'Edit' : 'View'}
                      </button>
                      {canManageLocations && (
                        <button
                          className="btn btn-sm btn-danger text-smd ml-2"
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this location?')) {
                              deleteLocation(location._id);
                            }
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default LocationManager; 